import { forwardRef, useCallback } from 'react';
import { parseShortcutKeys } from '@examdojo/core/tiptap-react-ui-components/lib/tiptap-utils';
import { useTiptapEditor } from '@examdojo/core/tiptap-react-ui-components/hooks/use-tiptap-editor';
import type { UseImageDownloadConfig } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/image-download-button';
import {
  IMAGE_DOWNLOAD_SHORTCUT_KEY,
  useImageDownload,
} from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/image-download-button';
import type { ButtonProps } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/button';
import { Button } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/button';
import { Badge } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/badge';

export interface ImageDownloadButtonProps extends Omit<ButtonProps, 'type'>, UseImageDownloadConfig {
  /**
   * Optional text to display alongside the icon.
   */
  text?: string;
  /**
   * Optional show shortcut keys in the button.
   * @default false
   */
  showShortcut?: boolean;
}

export function ImageDownloadShortcutBadge({ shortcutKeys = IMAGE_DOWNLOAD_SHORTCUT_KEY }: { shortcutKeys?: string }) {
  return <Badge>{parseShortcutKeys({ shortcutKeys })}</Badge>;
}

/**
 * Button component for downloading images from a Tiptap editor.
 * Only appears when an image is selected in the editor.
 *
 * For custom button implementations, use the `useImageDownload` hook instead.
 */
export const ImageDownloadButton = forwardRef<HTMLButtonElement, ImageDownloadButtonProps>(
  (
    {
      editor: providedEditor,
      text,
      hideWhenUnavailable = false,
      onDownloaded,
      resolveFileUrl,
      showShortcut = false,
      onClick,
      children,
      ...buttonProps
    },
    ref,
  ) => {
    const { editor } = useTiptapEditor(providedEditor);
    const { isVisible, canDownload, handleDownload, label, shortcutKeys, Icon } = useImageDownload({
      editor,
      hideWhenUnavailable,
      onDownloaded,
      resolveFileUrl,
    });

    const handleClick = useCallback(
      async (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        await handleDownload();
      },
      [handleDownload, onClick],
    );

    if (!isVisible) {
      return null;
    }

    return (
      <Button
        type="button"
        data-style="ghost"
        data-active-state="off"
        role="button"
        tabIndex={-1}
        disabled={!canDownload}
        data-disabled={!canDownload}
        aria-label={label}
        tooltip={label}
        onClick={handleClick}
        {...buttonProps}
        ref={ref}
      >
        {children ?? (
          <>
            <Icon className="tiptap-button-icon" />
            {text && <span className="tiptap-button-text">{text}</span>}
            {showShortcut && <ImageDownloadShortcutBadge shortcutKeys={shortcutKeys} />}
          </>
        )}
      </Button>
    );
  },
);

ImageDownloadButton.displayName = 'ImageDownloadButton';
